/* ==========================================================================
   Bastion Armor — cart drawer
   The design shipped a localStorage mock whose checkout button only printed
   "Demo build — checkout hands off to Shopify from here." This is that hand-off:
   the real Shopify AJAX Cart API, with the drawer markup re-rendered server-side
   through the Section Rendering API so Liquid stays the source of truth for
   money formatting, discounts and line-item properties.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var DRAWER_SECTION = 'cart-drawer';
  /* The header carries the cart count badge, so it has to be re-rendered
     alongside the drawer — the drawer section alone has no count element and
     the badge would sit stale until the next full page load. */
  var HEADER_SECTION = 'header';
  var SECTIONS = DRAWER_SECTION + ',' + HEADER_SECTION;

  function url(path) { return ROOT.replace(/\/$/, '') + path; }

  var drawer, backdrop, toastEl, toastTimer;

  /* ---------------------------------------------------------------- toast -- */
  function toast(msg, duration) {
    if (!toastEl) return;
    clearTimeout(toastTimer);
    toastEl.textContent = msg;
    toastEl.classList.add('is-on');
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-on'); }, duration || 2600);
  }

  /* --------------------------------------------------------------- drawer -- */
  function open() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var close = drawer.querySelector('.ba-drawer__close');
    if (close) close.focus();
  }

  function close() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function busy(on) {
    if (drawer) drawer.classList.toggle('is-busy', !!on);
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* True only when the mutation response actually carried rendered section
     HTML. /cart/add.js and /cart/change.js return `sections: {}` (empty) for
     sections declared statically in the layout rather than in a JSON template,
     so callers must fall back to refresh() — the GET Section Rendering API,
     which renders them reliably — whenever this is false. */
  function hasSections(sections) {
    return sections && Object.keys(sections).length > 0;
  }

  /* Swap in freshly rendered markup. `sections` is the object returned by the
     Section Rendering API, keyed by section id. */
  function render(sections) {
    if (!sections) return;

    if (sections[DRAWER_SECTION] && drawer) {
      var fresh = parse(sections[DRAWER_SECTION]).querySelector('[data-ba="drawer"]');
      if (fresh) {
        var wasOpen = drawer.classList.contains('is-open');
        drawer.innerHTML = fresh.innerHTML;
        drawer.classList.toggle('is-open', wasOpen);
      }
    }

    if (sections[HEADER_SECTION]) {
      var freshCount = parse(sections[HEADER_SECTION]).querySelector('[data-ba="cartCount"]');
      var liveCount = document.querySelector('[data-ba="cartCount"]');
      if (freshCount && liveCount) liveCount.textContent = freshCount.textContent;
    }
  }

  function refresh() {
    return fetch(url('/?sections=' + SECTIONS), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) { render(data); });
  }

  /* ------------------------------------------------------------ mutations -- */
  function add(variantId, quantity, button) {
    if (!variantId) {
      toast('That piece is out of stock.');
      return Promise.resolve();
    }
    if (button) button.classList.add('is-busy');
    busy(true);

    return fetch(url('/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: quantity || 1 }],
        sections: SECTIONS
      })
    })
      .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
      .then(function (res) {
        if (!res.ok) {
          toast(res.body && res.body.description ? res.body.description : 'Could not add that piece.');
          return;
        }
        if (hasSections(res.body.sections)) return render(res.body.sections);
        return refresh();
      })
      .then(function () { open(); })
      .catch(function () { toast('Network error — please try again.'); })
      .finally(function () {
        busy(false);
        if (button) button.classList.remove('is-busy');
      });
  }

  function change(line, quantity) {
    busy(true);
    return fetch(url('/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections: SECTIONS })
    })
      .then(function (r) { return r.json(); })
      .then(function (body) {
        if (hasSections(body.sections)) return render(body.sections);
        return refresh();
      })
      .catch(function () { toast('Network error — please try again.'); })
      .finally(function () { busy(false); });
  }

  /* --------------------------------------------------------------- events -- */
  function onClick(e) {
    var t = e.target;
    if (!t.closest) return;

    var addBtn = t.closest('[data-ba-add]');
    if (addBtn) {
      e.preventDefault();
      add(addBtn.getAttribute('data-ba-add'), 1, addBtn);
      return;
    }
    if (t.closest('[data-ba-open-cart]')) { e.preventDefault(); open(); return; }
    if (t.closest('[data-ba-close-cart]')) { e.preventDefault(); close(); return; }

    var qty = t.closest('[data-ba-qty]');
    if (qty) {
      e.preventDefault();
      change(qty.getAttribute('data-ba-line'), qty.getAttribute('data-ba-qty'));
      return;
    }
    var rm = t.closest('[data-ba-remove]');
    if (rm) {
      e.preventDefault();
      change(rm.getAttribute('data-ba-remove'), 0);
      return;
    }
    var checkout = t.closest('[data-ba-checkout]');
    if (checkout) {
      if (checkout.hasAttribute('data-ba-empty')) {
        e.preventDefault();
        toast('Your armory is empty.');
      }
      /* otherwise it is a real submit to /cart — let it through */
    }
  }

  function onKey(e) {
    if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) close();
  }

  /* ----------------------------------------------------------------- init -- */
  function boot() {
    drawer = document.querySelector('[data-ba="drawer"]');
    backdrop = document.querySelector('[data-ba="backdrop"]');
    toastEl = document.querySelector('[data-ba="toast"]');
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.BastionCart = { open: open, close: close, add: add, refresh: refresh, toast: toast };
})();
